import React, { useState } from 'react';
import { Target, Minus, Plus } from 'lucide-react';
import { FOCUS_SCALE } from '../utils/focusAttention';
import type { SessionFocusCapture } from '../lib/sessionFocusCapture';

/**
 * The end-of-session attention capture (M3 Phase C) — the act behind the Mind
 * area's **Focus & Attention** track.
 *
 * ⚠️ **This widget defines `FOCUS_SCALE`**, and `focusAttention.ts` pins it with a
 * test. Change the buttons here and that test fails on purpose: ER shipped a
 * scorer that assumed breathwork's axes were 1–5 when the slider writing them was
 * 0–100, and every unit test agreed with the bug because the fixtures were written
 * from the same assumption. The scale lives with the widget that writes it.
 *
 * **Skipping is a first-class answer.** A rating is what marks a session as focus
 * work: an unrated session never enters the track, so a two-minute toothbrush
 * timer stays out of it without anyone having to configure anything. That is why
 * Skip is a plain button and not a hidden escape hatch.
 *
 * It mints NOTHING. The session's XP was already awarded on stop, to its own
 * habit's domain.
 */

const ATTENTION_LABELS: Record<number, string> = {
  1: 'Scattered',
  2: 'Drifting',
  3: 'Steady',
  4: 'Sharp',
  5: 'Locked in',
};

interface SessionAttentionCaptureProps {
  /** Shown for context, e.g. "Deep work · 52m". */
  title: string;
  subtitle?: string;
  onSubmit: (focus: SessionFocusCapture) => void;
  onSkip: () => void;
}

export function SessionAttentionCapture({ title, subtitle, onSubmit, onSkip }: SessionAttentionCaptureProps) {
  const [attention, setAttention] = useState<number | null>(null);
  const [distractions, setDistractions] = useState(0);

  const scale = Array.from(
    { length: FOCUS_SCALE.max - FOCUS_SCALE.min + 1 },
    (_, i) => FOCUS_SCALE.min + i,
  );

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-6 space-y-6">
        <div className="space-y-1.5 text-center">
          <div className="flex items-center justify-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-bold text-foreground">How was your attention?</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {title}
            {subtitle && <span className="opacity-70"> · {subtitle}</span>}
          </p>
        </div>

        <div className="space-y-3">
          <div className="text-xs font-medium text-foreground">Focus</div>
          <div className="flex items-center gap-1.5">
            {scale.map(n => (
              <button
                key={n}
                onClick={() => setAttention(n)}
                aria-label={ATTENTION_LABELS[n]}
                aria-pressed={attention === n}
                className={`flex-1 h-10 rounded-lg border text-xs font-semibold transition-colors ${
                  attention != null && n <= attention
                    ? 'bg-primary border-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground h-4">
            {attention != null ? ATTENTION_LABELS[attention] : ''}
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-foreground">Times your attention broke off</div>
            <span className="text-sm font-semibold text-foreground tabular-nums">{distractions}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDistractions(d => Math.max(0, d - 1))}
              disabled={distractions === 0}
              aria-label="One fewer distraction"
              className="w-10 h-10 rounded-lg border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 flex items-center justify-center"
            >
              <Minus className="w-4 h-4" />
            </button>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
              {/* Against the rate that reads 0 resistance for an hour's work. */}
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.min(100, (distractions / 6) * 100)}%` }}
              />
            </div>
            <button
              onClick={() => setDistractions(d => d + 1)}
              aria-label="One more distraction"
              className="w-10 h-10 rounded-lg border border-border text-muted-foreground hover:text-foreground flex items-center justify-center"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={onSkip}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Not focus work
          </button>
          <button
            onClick={() => attention != null && onSubmit({ attention, distractions })}
            disabled={attention == null}
            className="flex-1 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
